import { Component, signal, inject, DestroyRef, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { ChatbotMessageListComponent } from '../chat-interface/components/chatbot-message-list/chatbot-message-list.component';
import { ApiService } from '../../../../core/services/api.service';
import { BreakpointService } from '../../../../core/services/breakpoint.service';
import { environment } from '../../../../../environments/environment';
import { apiBaseUrlFor, apiUrlFor } from '../../../../core/services/prism-new-routing';
import { asQueryEventStream } from '../../../../core/services/prism-new-legacy-shape';
import { CommonService } from '../../../../core/services/common.service';
import { AiBotService } from '../../../../core/services/ai-bot.service';
import { HeadlinePageService } from '../../../../core/services/headline-page.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { effect } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';


@Component({
  selector: 'app-ai-chat-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, ChatbotMessageListComponent],
  templateUrl: './ai-chat-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class AiChatPanelComponent {
  private _commonService = inject(CommonService);

  private apiService = inject(ApiService);
  private destroyRef = inject(DestroyRef);
  private breakpointService = inject(BreakpointService);

  isMobile = this.breakpointService.isMobile;

  @Input() useSpecializedApi = false;
  @Input() chatType: 'MONTHLY_REPORT_1' | 'CALL_AUDIT_HISTORY' | 'TRAINING_ANALYTICS' | 'ARRANGMENT_CRON' | 'CRON_PAYMENTS' | 'DAILY_CRON_PAYMENT' | 'SEO_STATS' | 'CAMPAIGN_METRICS' | 'SEO_OVERVIEW_GOOGLE' | 'SEO_OVERVIEW_META' | 'ADS_OVERVIEW' | 'INDUSTRY_CATEGORIZATION' | 'SOCIAL_MEDIA_PERFORMNACE' = 'MONTHLY_REPORT_1';
  @Input() layoutMode: 'floating' | 'fullscreen' = 'floating';
  @Input() set suggestions(value: string[]) {
    if (value && value.length > 0) {
      this.aiSuggestions.set(value);
    }
  }

  private aiBotService = inject(AiBotService);
  private headlinePageService = inject(HeadlinePageService);
  private productConfigService = inject(ProductConfigService);
  private router = inject(Router);
  /** product|page → the page's AI agent, looked up here for pages that never resolve their own. */
  private pageAgents = new Map<string, Promise<number | undefined>>();

  @Output() fullscreenChartRequested = new EventEmitter<void>();


  // Chat messages for AI panel
  chatMessages = signal<any[]>([]);

  // Typing indicator for AI response
  isTyping = signal(false);

  // Streaming response accumulator
  streamedResponse = signal('');

  // AI Suggestion buttons (signal for reactive updates)
  aiSuggestions = signal<string[]>([
    `What is this week's progress?`,
    `Compare this month's progress with last month?`,
    `Compare business growth with contracts growth for last quarter?`,
    `What is this year's progress?`
  ]);

  // Static AI responses for suggestion buttons
  private aiResponses: Record<string, string> = {
    'Business Growth Analysis': `# Business Growth Analysis

**Business growth trajectory decelerated automatically, moving from 0.0 to 0.0 [-100.00%]**`,
    'Contract Types Performance': `# Current Month Campaign Metrics`
  };

  userInput = signal('');
  conversationId: any;
  sessionId: string | null = null;
  isSessionModified = signal(false);
  /** The server holds `sessionId`: a query reached it (which creates the session) or it was loaded from history. */
  private sessionOnServer = false;

  private lastContextKey: string | null = null;

  constructor() {
    effect(() => {
      const currentKey = this._commonService.activeChatContextKey();

      // Save current state before switching to new context
      if (this.lastContextKey && this.lastContextKey !== currentKey) {
        this._commonService.saveChatState(this.lastContextKey, {
          messages: this.chatMessages(),
          sessionId: this.sessionId,
          conversationId: this.conversationId,
          suggestions: this.aiSuggestions(),
          isSessionModified: this.isSessionModified(),
          sessionOnServer: this.sessionOnServer
        });
      }

      // Load state for the new context
      if (currentKey) {
        const savedState = this._commonService.getChatState(currentKey);
        if (savedState) {
          this.chatMessages.set(savedState.messages || []);
          this.sessionId = savedState.sessionId || null;
          this.conversationId = savedState.conversationId || null;
          
          const isInteractive = savedState.isSessionModified || (savedState.messages && savedState.messages.length > 0);
          if (isInteractive) {
            this.aiSuggestions.set(savedState.suggestions || []);
          } else {
            const initialSuggestions = this._commonService.aiSuggestions();
            if (initialSuggestions && initialSuggestions.length > 0) {
              this.aiSuggestions.set(initialSuggestions);
            }
          }
          
          this.isSessionModified.set(savedState.isSessionModified || false);
          this.sessionOnServer = !!savedState.sessionOnServer;
        } else {
          this.resetChat();
          // After reset, if we have fresh suggestions in the common service, re-apply them
          const initialSuggestions = this._commonService.aiSuggestions();
          if (initialSuggestions && initialSuggestions.length > 0) {
            this.aiSuggestions.set(initialSuggestions);
          }
        }
      }

      this.lastContextKey = currentKey;
    });

    effect(() => {
      const agentId = this._commonService.activeAgentId();
      // When agentId changes (including to null), reset the chat
      //this.resetChat();
    });
  }

  ngOnInit() {
  }

  ngOnDestroy(): void {
    this.closeChatSession();
  }

  generateId(type: string): string {
    return type + '-' + Math.random().toString(36).substr(2, 9);
  }

  private getCsrfToken(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'prism-auth_csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  resetChat() {
    this.chatMessages.set([]);
    this.sessionId = null;
    this.conversationId = null;
    this.streamedResponse.set('');
    this.aiSuggestions.set([]);
    this.isTyping.set(false);
    this.isSessionModified.set(false);
    this.sessionOnServer = false;
  }


  sendQuery(suggestionText: string): void {
    if (!suggestionText?.trim()) return;

    this.isSessionModified.set(true);
    this.userInput.set('');

    // --- Common UI Setup ---
    this.chatMessages.update(messages => [
      ...messages,
      {
        from: 'user',
        text: suggestionText,
        timestamp: new Date().toISOString()
      }
    ]);

    this.isTyping.set(true);
    this.streamedResponse.set('');
    this.aiSuggestions.set([]);

    if (this.useSpecializedApi) {
      const payload = {
        message: suggestionText,
        thread_id: this.sessionId || null,
        streaming: true
      };
      this.executeSpecializedQuery(suggestionText, payload);
    } else {
      const queryId = this.generateId('query');
      if (!this.sessionId) {
        this.sessionId = this.generateId('session');
      }

      const payload = {
        queryId,
        sessionId: this.sessionId,
        conversationId: this.conversationId || null,
        query: suggestionText,
        product: this.apiService.selectedProductId(),
        isStream: true,
        type: this.chatType,
      };

      // Add queryId to the last message we just added
      this.chatMessages.update(messages => {
        const last = messages[messages.length - 1];
        if (last && last.from === 'user' && !last.queryId) {
          last.queryId = queryId;
        }
        return [...messages];
      });

      void this.agentIdForQuery().then((agentId) => {
        // prism-new requires `agentId` on every query: never send one without it.
        if (!agentId && apiBaseUrlFor('POST', 'summary/query') === environment.prismNew.apiUrl) {
          this.handleApiError('Ask Prism AI is not set up for this page yet.');
          return;
        }
        this.executeStandardQuery(suggestionText, { ...payload, agentId });
      });
    }
  }

  /**
   * The page's AI agent for `summary/query` (prism-new rejects a query without one): the agent the page
   * resolved, else one looked up here for the current page, which also covers a send before the page's
   * own lookup has answered.
   */
  private async agentIdForQuery(): Promise<number | undefined> {
    await this._commonService.agentResolvePending;
    const active = this._commonService.activeAgentId();
    if (active) return active;
    const productId = this.apiService.selectedProductId();
    const route = this.router.url.split('?')[0];
    const configs = this.headlinePageService.getAllConfigs();
    // Dashboard pages moved menus but kept their last segment (/dashboard/campaign-stats → /digital-insights/campaign-stats);
    // only those fall back to it, so a generic segment (e.g. /business) never borrows another page's agent.
    const lastSegment = (path: string) => path.split('?')[0].split('/').filter(Boolean).pop();
    const page = configs.find((c) => route.endsWith(c.route) || route.includes(c.route))
      ?? configs.find((c) => c.route.startsWith('/dashboard/') && lastSegment(c.route) === lastSegment(route));
    if (!productId || !page) return undefined;
    const key = `${productId}|${page.key}`;
    if (!this.pageAgents.has(key)) {
      const productName = this.productConfigService.getProductName(productId).toLowerCase();
      this.pageAgents.set(key, firstValueFrom(this.aiBotService.resolveAgent(productName, page.key, 'ask_prism_ai', false))
        .then((res: any) => res?.agent_id || res?.data?.agent_id || undefined)
        .catch(() => undefined));
    }
    return this.pageAgents.get(key);
  }

  private executeSpecializedQuery(suggestionText: string, payload: any, isRetry: boolean = false): void {
    const url = `${environment.ai.baseUrl}/call-auditor/chatbot`;
    const accessToken = this.apiService.getAccessToken();
    const csrfToken = this.getCsrfToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'omit'
    })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 401 && !isRetry) {
            const errorData = await response.clone().json().catch(() => ({}));
            if (errorData.code === 'TOKEN_INVALID') {
              this.apiService.post('/auth/refresh', {}, { showLoader: false }).subscribe({
                next: () => {
                  this.executeSpecializedQuery(suggestionText, payload, true);
                },
                error: () => {
                  this.isTyping.set(false);
                  this.handleApiError('Session expired. Please login again.');
                }
              });
              return;
            }
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        this.sessionOnServer = true;

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) throw new Error('Response body is not readable');

        let buffer = '';
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              this.isTyping.set(false);
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.substring(6);
                if (data === '[DONE]') {
                  this.isTyping.set(false);
                  continue;
                }

                try {
                  const parsed = JSON.parse(data);
                  if (parsed.type === 'content' && parsed.delta) {
                    this.appendToLastMessage(parsed.delta);
                  } else if (parsed.type === 'thread_id' && parsed.thread_id) {
                    this.sessionId = parsed.thread_id;
                  } else if (parsed.type === 'suggestions' && parsed.suggestions) {
                    this.aiSuggestions.set(parsed.suggestions);
                  } else if (parsed.type === 'final' && parsed.data) {
                    if (parsed.data.suggestions) this.aiSuggestions.set(parsed.data.suggestions);
                    if (parsed.data.thread_id) this.sessionId = parsed.data.thread_id;
                  }
                } catch (e) {
                  if (data) this.appendToLastMessage(data);
                }
              }
            }
          }
        } catch (error) {
          console.error('Streaming Error:', error);
          this.isTyping.set(false);
        }
      })
      .catch((err) => {
        console.error('Chatbot API Error:', err);
        this.isTyping.set(false);
      });
  }

  private executeStandardQuery(suggestionText: string, payload: any, isRetry: boolean = false): void {
    const url = apiUrlFor('POST', 'summary/query');
    const accessToken = this.apiService.getAccessToken();
    const deviceId = this.apiService.getDeviceId();
    const csrfToken = this.getCsrfToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    if (deviceId) {
      headers['x-device-id'] = deviceId;
    }

    fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'include'
    })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 401 && !isRetry) {
            const errorData = await response.clone().json().catch(() => ({}));
            // Old API: code TOKEN_INVALID; prism-new: TOKEN_EXPIRED with action REFRESH_TOKEN in error.details.
            const action = errorData.action ?? errorData.error?.details?.action;
            if ((errorData.code ?? errorData.error?.code) === 'TOKEN_INVALID' || action === 'REFRESH_TOKEN') {
              this.apiService.post('/auth/refresh', {}, { showLoader: false }).subscribe({
                next: () => {
                  this.executeStandardQuery(suggestionText, payload, true);
                },
                error: () => {
                  this.isTyping.set(false);
                  this.handleApiError('Session expired. Please login again.');
                }
              });
              return;
            }
          }
          // Say why in the chat rather than just stopping the typing indicator.
          const errorBody = await response.clone().json().catch(() => null);
          console.error('Chatbot API Error:', response.status, errorBody);
          this.handleApiError(
            errorBody?.error?.message ?? (typeof errorBody?.error === 'string' ? errorBody.error : `Sorry, the request failed (${response.status}).`));
          return;
        }
        this.sessionOnServer = true;

        const reader = (await asQueryEventStream(response)).body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('Response body is not readable');
        }

        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              this.isTyping.set(false);
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            let currentEventType = 'message';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                currentEventType = line.substring(7).trim();
                continue;
              }

              if (line.startsWith('data: ')) {
                const data = line.substring(6);

                if (currentEventType === 'end' || data === '[DONE]') {
                  this.isTyping.set(false);
                  currentEventType = 'message';
                  continue;
                }

                if (currentEventType === 'suggestions') {
                  const suggestions = data.split('|').map(s => s.trim()).filter(s => s.length > 0);
                  this.conversationId = suggestions.pop();
                  if (suggestions.length > 0) {
                    this.aiSuggestions.set(suggestions);
                  }
                  currentEventType = 'message';
                  continue;
                }

                if (currentEventType === 'start') {
                  try {
                    const parsed = JSON.parse(data);
                    if (parsed.sessionId) this.sessionId = parsed.sessionId;
                    if (parsed.conversationId) this.conversationId = parsed.conversationId;
                  } catch (e) { }
                  currentEventType = 'message';
                  continue;
                }

                let parsed = null;
                let isStructuredMessage = false;

                try {
                  parsed = JSON.parse(data);
                  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    if (parsed.message || parsed.suggestions || parsed.suggestedQuestions || parsed.conversationId || parsed.event) {
                      isStructuredMessage = true;
                    }
                  }
                } catch (e) { }

                if (isStructuredMessage) {
                  if (parsed.event === 'suggestions' && parsed.data) {
                    this.aiSuggestions.set(Array.isArray(parsed.data) ? parsed.data : [parsed.data]);
                    continue;
                  }

                  if (parsed.suggestions || parsed.suggestedQuestions) {
                    this.aiSuggestions.set(parsed.suggestions || parsed.suggestedQuestions);
                  }
                  if (parsed.conversationId) {
                    this.conversationId = parsed.conversationId;
                  }

                  if (parsed.message) {
                    const isStreamingIndicator = parsed.message === 'textStreaming...' ||
                      parsed.message.includes('textStreaming');

                    if (!isStreamingIndicator) {
                      this.appendToLastMessage(parsed.message);
                    }
                  }
                } else {
                  if (currentEventType === 'message' || currentEventType === 'data') {
                    this.appendToLastMessage(data);
                  }
                }
              }
            }
          }
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('SSE Error:', error);
            this.handleApiError('Sorry, I encountered an error processing your request.');
          }
        }
      });
  }

  private handleApiError(errorMessage: string): void {
    this.isTyping.set(false);
    this.chatMessages.update(messages => [
      ...messages,
      {
        from: 'ai',
        text: errorMessage,
        queryId: 'error-' + Date.now(),
        timestamp: new Date().toISOString()
      }
    ]);
  }


  // Helper to append text to the last AI message
  private appendToLastMessage(content: string): void {
    const currentText = this.streamedResponse();
    const updatedText = currentText + content;
    this.streamedResponse.set(updatedText);

    // If this is the start of a new message
    const messages = this.chatMessages();
    const lastMessage = messages[messages.length - 1];

    if (this.isTyping() && (!lastMessage || lastMessage.from === 'user')) {
      // We were typing, time to convert to real message
      this.isTyping.set(false);
      this.chatMessages.update(msgs => [
        ...msgs,
        {
          from: 'ai',
          text: updatedText,
          queryId: msgs[msgs.length - 1]?.queryId || 'ai-' + Date.now(),
          timestamp: new Date().toISOString()
        }
      ]);
    } else if (lastMessage && lastMessage.from === 'ai') {
      // Update existing AI message
      this.chatMessages.update(msgs => {
        const newMsgs = [...msgs];
        newMsgs[newMsgs.length - 1] = {
          ...newMsgs[newMsgs.length - 1],
          text: updatedText
        };
        return newMsgs;
      });
    }
  }

  closeChatSession() {
    // A query that was blocked or failed never created the session; closing it would 404.
    if (!this.sessionId || !this.sessionOnServer || !this.isSessionModified()) return;
    if (this.chatMessages().length === 0) return;
    this.apiService.put(`/summary/sessions/${this.sessionId}`, { status: 'COMPLETED' }, { showLoader: false, showErrorToaster: true }).subscribe({
      next: () => {
        // console.log('Chat session closed successfully');
      },
      error: (error) => {
        console.error('Error closing chat session:', error);
      }
    })
  }

  loadChatSession(session: any) {
    const sId = session.id || session.sessionId;
    if (!sId) return;

    this.isTyping.set(true); // Optional: visual feedback
    this.chatMessages.set([]); // Clear existing
    this.aiSuggestions.set([]);

    this.apiService.get(`/summary/history/${sId}`).subscribe({
      next: (res: any) => {
        let history: any[] = [];
        if (res && res.data && Array.isArray(res.data.chatHistory)) {
          history = res.data.chatHistory;
        } else if (res && res.data && Array.isArray(res.data)) {
          history = res.data;
        } else if (res && Array.isArray(res.chatHistory)) {
          history = res.chatHistory;
        } else if (res && Array.isArray(res)) {
          history = res;
        } else if (res && res.results) {
          history = Array.isArray(res.results) ? res.results : [];
        }

        this.sessionId = sId;
        this.sessionOnServer = true;
        this.conversationId = session.conversationId || (res?.data?.conversationId) || history[0]?.conversationId || null;

        const msgs: any[] = [];

        history.forEach((item: any) => {
          // If the item has a specific 'role' (old array of messages format)
          if (item.role || item.type || item.sender) {
            let from = 'ai';
            if (item.role === 'user' || item.role === 'USER' || item.type === 'USER' || item.type === 'user' || item.sender === 'user' || item.isUser) {
              from = 'user';
            }
            msgs.push({
              from,
              text: item.message || item.text || item.content || item.query || item.response || '',
              queryId: item.id || item.queryId || 'hist-' + Math.random().toString(36).substr(2, 9),
              timestamp: item.createdAt || item.timestamp || new Date().toISOString()
            });
          }
          // New format: Q&A pair in one object
          else if (item.query || item.aiResponse) {
            if (item.query) {
              msgs.push({
                from: 'user',
                text: item.query,
                queryId: item.queryId || 'hist-usr-' + Math.random().toString(36).substr(2, 9),
                timestamp: item.timestamp || item.createdAt || new Date().toISOString()
              });
            }
            if (item.aiResponse) {
              msgs.push({
                from: 'ai',
                text: item.aiResponse,
                queryId: 'hist-ai-' + Math.random().toString(36).substr(2, 9),
                timestamp: item.timestamp || item.createdAt || new Date().toISOString()
              });
            }
          }
        });

        // Set suggestions if present in the last item
        if (history.length > 0) {
          const lastItem = history[history.length - 1];
          if (lastItem.suggestedQuestions?.length || lastItem.suggestions?.length) {
            this.aiSuggestions.set(lastItem.suggestedQuestions || lastItem.suggestions);
          }
        }

        this.chatMessages.set(msgs);
        this.isTyping.set(false);
        this.isSessionModified.set(false);
      },
      error: (err) => {
        console.error('Error loading chat history:', err);
        this.isTyping.set(false);
      }
    });
  }

}
